import React, { useState, useEffect } from 'react';
import { reportsAPI, adminAPI, deletedLeadsAPI, leadSourceAPI } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, Filter, FileText, DollarSign, Users, CreditCard, Clock, Calendar, FileSpreadsheet, Trash2, RotateCcw, Search } from 'lucide-react';

const STATUSES = ['New', 'Contact Attempted', 'Connected', 'Contacted', 'Interested', 'Counselling Scheduled', 'Demo Scheduled', 'Demo Attended', 'Follow-up', 'Admission Likely', 'Converted', 'Future Prospect', 'Not Interested', 'Lost'];
const SOURCES = ['Website', 'Social Media', 'Referral', 'Walk-in', 'Phone Call', 'Google', 'Meta', 'Instagram'];
const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Net Banking', 'Cheque'];
// Report types that use the new branded export (Excel/PDF + rich filters)
const EXPORT_TYPES = new Set(['leads', 'enrollments', 'income', 'expenses', 'pending_payments']);

const ReportsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [reportType, setReportType] = useState('leads');
  const [filters, setFilters] = useState({
    status: 'All',
    source: 'All',
    program_id: 'All',
    branch_id: 'All',
    start_date: '',
    end_date: '',
    program_ids: [],
    payment_modes: [],
    statuses: [],
    sources: [],
  });
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [generating, setGenerating] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'Admin';
  const isBranchAdmin = user.role === 'Branch Admin';
  const isCounsellor = user.role === 'Counsellor';
  const isFDE = user.role === 'Front Desk Executive';

  // Define all report types
  const allReportTypes = [
    { id: 'leads', label: 'Leads Report', icon: Users, description: 'Lead ID, Name, Phone, Course, Source', roles: ['Admin', 'Branch Admin', 'Counsellor', 'Front Desk Executive'] },
    { id: 'enrollments', label: 'Enrollments Report', icon: FileText, description: 'Student enrollments and program details', roles: ['Admin', 'Branch Admin', 'Front Desk Executive'] },
    { id: 'income', label: 'Monthly Collection Report', icon: DollarSign, description: 'Receipt No, Name, Number, Amount, Mode, Date', roles: ['Admin', 'Branch Admin', 'Front Desk Executive'] },
    { id: 'expenses', label: 'Expense Report', icon: CreditCard, description: 'Name, Date, Amount, Description', roles: ['Admin', 'Branch Admin'] },
    { id: 'pending_payments', label: 'Pending Payment Report', icon: Clock, description: 'Name, Course, Number, Final/Paid/Pending Fee', roles: ['Admin', 'Branch Admin', 'Front Desk Executive'] },
    { id: 'fee_collection', label: 'Monthly Fee Collection', icon: Calendar, description: 'Installments due in selected month', roles: ['Admin', 'Branch Admin'] },
    { id: 'deleted_leads', label: 'Deleted Leads', icon: Trash2, description: 'View and restore deleted leads', roles: ['Admin', 'Branch Admin'] },
  ];

  // Deleted Leads State
  const [deletedLeads, setDeletedLeads] = useState([]);
  const [deletedLeadsLoading, setDeletedLeadsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoringId, setRestoringId] = useState(null);

  // Filter report types based on user role
  const reportTypes = allReportTypes.filter(type => type.roles.includes(user.role));

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (reportType === 'deleted_leads') {
      fetchDeletedLeads();
    }
  }, [reportType]);

  const fetchOptions = async () => {
    try {
      const [programRes, branchRes] = await Promise.all([
        adminAPI.getPrograms(),
        adminAPI.getBranches(),
      ]);
      setPrograms(programRes.data);
      setBranches(branchRes.data);
      leadSourceAPI.getAll().then((r) => setLeadSources((r.data || []).map((s) => s.name))).catch(() => {});
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchDeletedLeads = async () => {
    setDeletedLeadsLoading(true);
    try {
      const response = await deletedLeadsAPI.getAll();
      setDeletedLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch deleted leads');
    } finally {
      setDeletedLeadsLoading(false);
    }
  };

  const handleRestoreLead = async (leadId) => {
    setRestoringId(leadId);
    try {
      await deletedLeadsAPI.restore(leadId);
      toast.success('Lead restored successfully!');
      fetchDeletedLeads();
    } catch (error) {
      toast.error('Failed to restore lead');
    } finally {
      setRestoringId(null);
    }
  };

  const filteredDeletedLeads = deletedLeads.filter(lead => 
    lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.number?.includes(searchTerm)
  );

  const handleGenerateReport = async (customReportType = null, customStartDate = null) => {
    setGenerating(true);
    try {
      const actualReportType = customReportType || reportType;

      // Branded Excel/PDF export path (rich filters)
      if (EXPORT_TYPES.has(actualReportType) && !customStartDate) {
        const params = { report_type: actualReportType, format: exportFormat };
        if (filters.branch_id && filters.branch_id !== 'All') params.branch_id = filters.branch_id;
        if (filters.start_date) params.start_date = filters.start_date;
        if (filters.end_date) params.end_date = filters.end_date;
        if (actualReportType === 'leads') {
          if (filters.statuses.length) params.statuses = filters.statuses.join(',');
          if (filters.sources.length) params.sources = filters.sources.join(',');
          if (filters.program_ids.length) params.program_ids = filters.program_ids.join(',');
        }
        if (actualReportType === 'enrollments' && filters.program_ids.length) {
          params.program_ids = filters.program_ids.join(',');
        }
        if (actualReportType === 'income' && filters.payment_modes.length) {
          params.payment_modes = filters.payment_modes.join(',');
        }
        const response = await reportsAPI.exportReport(params);
        const ext = exportFormat === 'pdf' ? 'pdf' : 'xlsx';
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${actualReportType}_report_${new Date().toISOString().split('T')[0]}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`Report downloaded as ${ext.toUpperCase()}!`);
        return;
      }

      // Legacy CSV path (fee_collection quick download etc.)
      const cleanFilters = { report_type: actualReportType };
      if (customStartDate) {
        cleanFilters.start_date = customStartDate;
      } else {
        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) return;
          if (value && value !== 'All') cleanFilters[key] = value;
        });
      }
      const response = await reportsAPI.generateReport(cleanFilters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${actualReportType}_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleQuickFeeCollection = () => {
    const currentMonthStart = new Date().toISOString().slice(0, 7) + '-01';
    handleGenerateReport('fee_collection', currentMonthStart);
  };

  const resetFilters = () => {
    setFilters({
      status: 'All',
      source: 'All',
      program_id: 'All',
      branch_id: 'All',
      start_date: '',
      end_date: '',
      program_ids: [],
      payment_modes: [],
      statuses: [],
      sources: [],
    });
  };

  const toggleInArray = (key, value) => {
    setFilters((prev) => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2">Reports</h1>
        <p className="text-slate-600">Generate custom reports with filters</p>
      </div>

      {/* Report Type Selection */}
      <Card className="border-slate-200 shadow-soft">
        <CardHeader>
          <CardTitle>Select Report Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {reportTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setReportType(type.id)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  reportType === type.id
                    ? 'border-slate-900 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`report-type-${type.id}`}
              >
                <type.icon className={`w-6 h-6 mb-2 ${reportType === type.id ? 'text-slate-900' : 'text-slate-500'}`} />
                <p className="font-medium text-sm">{type.label}</p>
                <p className="text-xs text-slate-500 mt-1">{type.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Monthly Fee Collection Download - Branch Admin only */}
      {(isBranchAdmin || isSuperAdmin) && (
        <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <FileSpreadsheet className="w-5 h-5" />
              Monthly Fee Collection Report
            </CardTitle>
            <CardDescription className="text-green-700">
              Download installments due for any month - Available on 1st of every month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-green-800">
                  <strong>Current Month:</strong> {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Includes: Student ID, Name, Phone, Course, Total Fee, Paid Fee, Due Amount, Due Date
                </p>
              </div>
              <Button 
                onClick={handleQuickFeeCollection}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={generating}
                data-testid="quick-fee-collection-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download {new Date().toLocaleString('default', { month: 'short' })} Report
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-slate-200 shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Branch Filter - Super Admin only */}
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select
                  value={filters.branch_id}
                  onValueChange={(value) => setFilters({ ...filters, branch_id: value })}
                >
                  <SelectTrigger data-testid="branch-filter">
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Branches</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date Filters */}
            {reportType !== 'fee_collection' && (
              <>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    data-testid="start-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    data-testid="end-date-input"
                  />
                </div>
              </>
            )}

            {/* Month Picker for Fee Collection Report */}
            {reportType === 'fee_collection' && (
              <div className="space-y-2">
                <Label>Select Month</Label>
                <Input
                  type="month"
                  value={filters.start_date ? filters.start_date.slice(0, 7) : new Date().toISOString().slice(0, 7)}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value + '-01' })}
                  data-testid="month-picker"
                />
                <p className="text-xs text-slate-500">Select the month to view all installments due</p>
              </div>
            )}
          </div>

          {/* Lead Status multi-select */}
          {reportType === 'leads' && (
            <div className="space-y-2" data-testid="status-multiselect">
              <Label>Lead Status {filters.statuses.length > 0 && <span className="text-xs text-slate-500">({filters.statuses.length} selected)</span>}</Label>
              <p className="text-xs text-slate-500">Tick one or more (e.g. Lost + Converted + New). Leave all unticked to include every status.</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.filter((s) => s !== 'All').map((status) => {
                  const selected = filters.statuses.includes(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      data-testid={`status-chip-${status.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => toggleInArray('statuses', status)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${selected ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700 hover:border-slate-400'}`}
                    >
                      {status}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Lead Source multi-select */}
          {reportType === 'leads' && (
            <div className="space-y-2" data-testid="source-multiselect">
              <Label>Lead Source {filters.sources.length > 0 && <span className="text-xs text-slate-500">({filters.sources.length} selected)</span>}</Label>
              <p className="text-xs text-slate-500">Tick one or more sources. Leave all unticked to include every source.</p>
              <div className="flex flex-wrap gap-2">
                {(leadSources.length ? leadSources : SOURCES).map((source) => {
                  const selected = filters.sources.includes(source);
                  return (
                    <button
                      key={source}
                      type="button"
                      data-testid={`source-chip-${source.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => toggleInArray('sources', source)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${selected ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700 hover:border-slate-400'}`}
                    >
                      {source}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Multi-course selector (Leads & Enrollments) */}
          {(reportType === 'leads' || reportType === 'enrollments') && (
            <div className="space-y-2" data-testid="course-multiselect">
              <Label>Courses {filters.program_ids.length > 0 && <span className="text-xs text-slate-500">({filters.program_ids.length} selected)</span>}</Label>
              <p className="text-xs text-slate-500">Leave all unchecked to include every course, or pick one/multiple.</p>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                {programs.map((program) => {
                  const selected = filters.program_ids.includes(program.id);
                  return (
                    <button
                      key={program.id}
                      type="button"
                      data-testid={`course-chip-${program.id}`}
                      onClick={() => toggleInArray('program_ids', program.id)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${selected ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700 hover:border-slate-400'}`}
                    >
                      {program.name}
                    </button>
                  );
                })}
                {programs.length === 0 && <span className="text-sm text-slate-400">No courses defined.</span>}
              </div>
            </div>
          )}

          {/* Payment mode selector (Fee / Collection report) */}
          {reportType === 'income' && (
            <div className="space-y-2" data-testid="payment-mode-multiselect">
              <Label>Payment Mode {filters.payment_modes.length > 0 && <span className="text-xs text-slate-500">({filters.payment_modes.length} selected)</span>}</Label>
              <p className="text-xs text-slate-500">Leave all unchecked to include all modes.</p>
              <div className="flex flex-wrap gap-2">
                {PAYMENT_MODES.map((mode) => {
                  const selected = filters.payment_modes.includes(mode);
                  return (
                    <button
                      key={mode}
                      type="button"
                      data-testid={`payment-mode-${mode.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => toggleInArray('payment_modes', mode)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${selected ? 'border-green-600 bg-green-600 text-white' : 'border-slate-300 text-slate-700 hover:border-green-400'}`}
                    >
                      {mode}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Format selector */}
          {EXPORT_TYPES.has(reportType) && (
            <div className="space-y-2" data-testid="format-selector">
              <Label>Download Format</Label>
              <div className="flex gap-2">
                {[{ v: 'xlsx', label: 'Excel (.xlsx)', icon: FileSpreadsheet }, { v: 'pdf', label: 'PDF', icon: FileText }].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`format-${opt.v}`}
                    onClick={() => setExportFormat(opt.v)}
                    className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${exportFormat === opt.v ? 'border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-400' : 'border-slate-300 text-slate-600 hover:border-blue-300'}`}
                  >
                    <opt.icon className="w-4 h-4" /> {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetFilters}>
              Reset Filters
            </Button>
            <Button
              onClick={() => handleGenerateReport()}
              disabled={generating}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="generate-report-button"
            >
              <Download className="w-4 h-4 mr-2" />
              {generating ? 'Generating...' : 'Download Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Deleted Leads Section */}
      {reportType === 'deleted_leads' && (
        <Card className="border-slate-200 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Deleted Leads
            </CardTitle>
            <CardDescription>
              View and restore previously deleted leads
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="deleted-leads-search"
              />
            </div>

            {deletedLeadsLoading ? (
              <div className="text-center py-8 text-slate-500">Loading deleted leads...</div>
            ) : filteredDeletedLeads.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {searchTerm ? 'No matching deleted leads found' : 'No deleted leads'}
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {filteredDeletedLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <p className="font-semibold">{lead.name}</p>
                      <p className="text-sm text-slate-500">{lead.email} • {lead.number}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{lead.program || 'N/A'}</Badge>
                        <Badge className="bg-red-100 text-red-700 text-xs">{lead.status || 'Deleted'}</Badge>
                      </div>
                      {lead.deleted_at && (
                        <p className="text-xs text-slate-400 mt-1">
                          Deleted: {new Date(lead.deleted_at).toLocaleDateString()}
                          {lead.deleted_by_name && ` by ${lead.deleted_by_name}`}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestoreLead(lead.id)}
                      disabled={restoringId === lead.id}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      data-testid={`restore-lead-${lead.id}`}
                    >
                      <RotateCcw className={`w-4 h-4 mr-1 ${restoringId === lead.id ? 'animate-spin' : ''}`} />
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Report Info */}
      <Card className="border-slate-200 shadow-soft">
        <CardHeader>
          <CardTitle>Report Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
            <p className="text-sm text-slate-600">
              Fee, Lead, Enrollment, Expense & Pending reports download as branded <strong>Excel</strong> or <strong>PDF</strong> with the ETI logo, a titled header and a formatted table
            </p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
            <p className="text-sm text-slate-600">
              {isBranchAdmin 
                ? 'Reports are automatically filtered to your branch data only'
                : 'Super Admin can generate reports for all branches or filter by specific branch'
              }
            </p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
            <p className="text-sm text-slate-600">
              Use date filters to generate reports for specific time periods
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
